/**
 * NIGHTBOUND - ability catalogue.
 *
 * Only the four starting attacks are used in this milestone, but the shape is
 * the one combat will need: abilities have a power, a kind, status effects and
 * an optional essence cost (for abilities learned from consumed creatures).
 */

import type { SpeciesId } from './species'

export type AbilityKind = 'ATTACK' | 'STATUS' | 'SUPPORT' | 'PASSIVE'

export type StatusEffectId =
  | 'BLEED'
  | 'POISON'
  | 'STUN'
  | 'WEAKEN'
  | 'SLOW'
  | 'CORRUPTED'

export interface StatusEffectDefinition {
  id: StatusEffectId
  name: string
  description: string
  /** Damage/effect applied per turn. */
  magnitude: number
  durationTurns: number
  /** Applied to the target by default; false means it buffs the user. */
  targetsEnemy: boolean
}

export interface AbilityDefinition {
  key: string
  name: string
  description: string
  flavour: string
  kind: AbilityKind
  /** Base power used by the future damage formula. */
  power: number
  /** Accuracy 0-1. */
  accuracy: number
  cooldownTurns: number
  statusEffects: StatusEffectId[]
  /** Species that begin with this ability. */
  starterFor: SpeciesId[]
  /** Set once abilities can be learned from Essence. */
  essenceSource?: string
  slot: 'STARTER' | 'LEARNED' | 'MUTATION'
}

export const STATUS_EFFECTS: Record<StatusEffectId, StatusEffectDefinition> = {
  BLEED: {
    id: 'BLEED',
    name: 'Bleeding',
    description: 'Loses health at the end of each turn.',
    magnitude: 4,
    durationTurns: 3,
    targetsEnemy: true,
  },
  POISON: {
    id: 'POISON',
    name: 'Venom',
    description: 'Loses health each turn; worse the longer it lasts.',
    magnitude: 3,
    durationTurns: 4,
    targetsEnemy: true,
  },
  STUN: {
    id: 'STUN',
    name: 'Stunned',
    description: 'Cannot act this turn.',
    magnitude: 1,
    durationTurns: 1,
    targetsEnemy: true,
  },
  WEAKEN: {
    id: 'WEAKEN',
    name: 'Weakened',
    description: 'Attack reduced.',
    magnitude: 0.25,
    durationTurns: 3,
    targetsEnemy: true,
  },
  SLOW: {
    id: 'SLOW',
    name: 'Slowed',
    description: 'Speed reduced, acting later in the turn order.',
    magnitude: 0.3,
    durationTurns: 3,
    targetsEnemy: true,
  },
  CORRUPTED: {
    id: 'CORRUPTED',
    name: 'Corrupted',
    description: 'Nightmare influence. Reduces sanity and defence.',
    magnitude: 0.2,
    durationTurns: 4,
    targetsEnemy: true,
  },
}

export const ABILITIES: Record<string, AbilityDefinition> = {
  bite: {
    key: 'bite',
    name: 'Bite',
    description: 'A straightforward, committed bite.',
    flavour: 'It has done this before. To something.',
    kind: 'ATTACK',
    power: 14,
    accuracy: 0.95,
    cooldownTurns: 0,
    statusEffects: [],
    starterFor: ['DOG'],
    slot: 'STARTER',
  },
  claw: {
    key: 'claw',
    name: 'Claw',
    description: 'Fast raking strikes.',
    flavour: 'Four thin lines, drawn quickly.',
    kind: 'ATTACK',
    power: 13,
    accuracy: 0.97,
    cooldownTurns: 0,
    statusEffects: ['BLEED'],
    starterFor: ['CAT'],
    slot: 'STARTER',
  },
  peck: {
    key: 'peck',
    name: 'Peck',
    description: 'A precise, darting strike.',
    flavour: 'Small, sudden, and much harder than it looks.',
    kind: 'ATTACK',
    power: 10,
    accuracy: 0.93,
    cooldownTurns: 0,
    statusEffects: [],
    starterFor: ['BIRD'],
    slot: 'STARTER',
  },
  fang: {
    key: 'fang',
    name: 'Fang',
    description: 'A slow strike that leaves venom behind.',
    flavour: 'It waits for one perfect moment.',
    kind: 'ATTACK',
    power: 11,
    accuracy: 0.9,
    cooldownTurns: 1,
    statusEffects: ['POISON'],
    starterFor: ['SNAKE'],
    slot: 'STARTER',
  },
  // Future: wild abilities unlocked by Essence (data only for now).
  savage_bite: {
    key: 'savage_bite',
    name: 'Savage Bite',
    description: 'A killing bite that heals the user.',
    flavour: 'Learned from the wolf. Remembered in the body.',
    kind: 'ATTACK',
    power: 20,
    accuracy: 0.9,
    cooldownTurns: 2,
    statusEffects: ['BLEED'],
    starterFor: [],
    essenceSource: 'WOLF',
    slot: 'LEARNED',
  },
  ambush: {
    key: 'ambush',
    name: 'Ambush',
    description: 'Strikes first with greatly increased critical chance.',
    flavour: 'Learned from the lynx.',
    kind: 'ATTACK',
    power: 17,
    accuracy: 0.92,
    cooldownTurns: 2,
    statusEffects: [],
    starterFor: [],
    essenceSource: 'LYNX',
    slot: 'LEARNED',
  },
  dive: {
    key: 'dive',
    name: 'Dive',
    description: 'A high-speed pass that lowers the target\u2019s accuracy.',
    flavour: 'Learned from the crow.',
    kind: 'ATTACK',
    power: 12,
    accuracy: 0.98,
    cooldownTurns: 1,
    statusEffects: ['SLOW'],
    starterFor: [],
    essenceSource: 'CROW',
    slot: 'LEARNED',
  },
  venom_fang: {
    key: 'venom_fang',
    name: 'Venom Fang',
    description: 'Deep puncture that applies heavy venom.',
    flavour: 'Learned from the viper.',
    kind: 'ATTACK',
    power: 15,
    accuracy: 0.9,
    cooldownTurns: 2,
    statusEffects: ['POISON', 'WEAKEN'],
    starterFor: [],
    essenceSource: 'VIPER',
    slot: 'LEARNED',
  },
}

export const STARTER_ABILITY_BY_SPECIES: Record<SpeciesId, string> = {
  DOG: 'bite',
  CAT: 'claw',
  BIRD: 'peck',
  SNAKE: 'fang',
}

export function getAbility(key: string): AbilityDefinition | undefined {
  return ABILITIES[key]
}
